import React, { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { Grid, TextField } from '@mui/material';
import { getDestinationCurrency } from '../../../util/options';
import ErrorView from '../../../components/common/ErrorView';

import { Flag } from '../../../components/types';
import { FUND_TENANT_RATE } from '../../../graphql/rate';
import { FragmentType, useFragment } from '../../../__generated__';
import { ClientRate_ItemFragment } from './ClientRateItem';
import { getDestinationBalanceCurrency } from 'gmd-utilities-package';
import FormDialogContainer, {
  DialogBody,
} from '../../../components/common/FormDialogContainer';

type FundClientRateContainerProps = {
  open: boolean;
  tenantId: string;
  rate: FragmentType<typeof ClientRate_ItemFragment>;
  onClose: (itemChanged?: boolean) => void;
};

const FundClientRateContainer = (props: FundClientRateContainerProps) => {
  const { open, tenantId, onClose } = props;
  const rate = useFragment(ClientRate_ItemFragment, props.rate) || null;
  const [fundTenantRate, { loading, error }] = useMutation(FUND_TENANT_RATE);
  const [flag, setFlag] = useState<Flag | undefined>(
    getDestinationCurrency(rate?.destination || '')
  );
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [debitAmount, setDebitAmount] = useState<string>('');

  const [formError, setFormError] = useState<string>();
  const [balanceCurrency, setBalanceCurrency] = useState<string>(
    getDestinationBalanceCurrency(rate?.destination)
  );

  useEffect(() => {
    setCreditAmount('');
    setDebitAmount('');
    setFlag(getDestinationCurrency(rate?.destination || ''));
    setFormError('');
    setBalanceCurrency(getDestinationBalanceCurrency(rate?.destination));
  }, [rate]);

  function handleAmountChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    callBack: (change: string) => void
  ): void {
    callBack(e.currentTarget.value);
  }

  const allowSubmit = () => {
    if (!debitAmount && !creditAmount) return false;

    const debitAmountSet = debitAmount && +debitAmount > 0;
    const creditAmountSet = creditAmount && +creditAmount > 0;

    const result =
      balanceCurrency &&
      ((debitAmountSet && !creditAmountSet) ||
        (creditAmountSet && !debitAmountSet));
    return Boolean(result && !loading);
  };

  const handleClose = (itemChanged = false) => {
    setCreditAmount('');
    setDebitAmount('');
    onClose(itemChanged);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');

    if (allowSubmit()) {
      const credit = Math.abs(creditAmount ? +creditAmount : 0);
      const debit = Math.abs(debitAmount ? +debitAmount : 0);
      const amount = credit - debit;

      const result = await fundTenantRate({
        variables: {
          tenantId,
          currency: balanceCurrency,
          amount,
        },
      });
      if (result?.data?.fundTenantRate?.success) {
        handleClose(true);
      }
    } else {
      setFormError(
        'There was an issue with the form submission. Please contact system admin.'
      );
    }
  };

  const errorInfo = useMemo(() => {
    const msg = formError || error?.message;
    let hasError = false;
    if (msg) hasError = true;
    return {
      errorMessage: msg,
      hasError,
    };
  }, [formError, error]);
  return (
    <FormDialogContainer>
      <DialogBody
        open={open}
        disabled={errorInfo.hasError || !allowSubmit()}
        title={`Funding ${flag?.label || 'UNKOWN RATE'}`}
        contextTitle={''}
        onClose={handleClose}
        onSubmit={handleSubmit}
      >
        <Grid container>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              id="credit"
              name="credit"
              label={`Credit Amount`}
              value={creditAmount}
              onChange={(e) => handleAmountChange(e, setCreditAmount)}
              type="number"
              fullWidth
              variant="standard"
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              id="debit"
              name="debit"
              label={`Debit Amount`}
              value={debitAmount}
              onChange={(e) => handleAmountChange(e, setDebitAmount)}
              type="number"
              fullWidth
              variant="standard"
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </Grid>
          {errorInfo.errorMessage && (
            <Grid item xs={12}>
              <ErrorView message={errorInfo.errorMessage} />
            </Grid>
          )}
        </Grid>
      </DialogBody>
    </FormDialogContainer>
  );
};

export default FundClientRateContainer;
